import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const SOAPNoteForm = ({ open, onClose, onSuccess, patientId, user, visit = null }) => {
  const [formData, setFormData] = useState(visit ? {
    motif: visit.motif,
    soap: visit.soap,
    vitals: visit.vitals,
    diagnostic: visit.diagnostic || []
  } : {
    motif: '',
    soap: {
      subjectif: '',
      objectif: '',
      assessment: '',
      plan: ''
    },
    vitals: {
      ta_systolique: '',
      ta_diastolique: '',
      pouls: '',
      temperature: '',
      spo2: '',
      poids: '',
      taille: '',
      imc: ''
    },
    diagnostic: []
  });
  const [diagnosticText, setDiagnosticText] = useState(visit?.diagnostic?.join(', ') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate IMC automatically
  const calculateIMC = (poids, taille) => {
    if (poids && taille && taille > 0) {
      const tailleM = taille / 100;
      const imc = (poids / (tailleM * tailleM)).toFixed(1);
      return imc;
    }
    return '';
  };

  const handleVitalsChange = (field, value) => {
    const newVitals = { ...formData.vitals, [field]: value };

    // Auto-calculate IMC
    if (field === 'poids' || field === 'taille') {
      const imc = calculateIMC(
        field === 'poids' ? value : newVitals.poids,
        field === 'taille' ? value : newVitals.taille
      );
      newVitals.imc = imc;
    }

    setFormData({ ...formData, vitals: newVitals });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Convert vitals to numbers
      const vitalsData = {};
      Object.keys(formData.vitals).forEach(key => {
        const value = formData.vitals[key];
        if (value !== '' && value !== null) {
          vitalsData[key] = parseFloat(value);
        }
      });

      const dataToSend = {
        patient_id: patientId,
        motif: formData.motif,
        soap: formData.soap,
        vitals: vitalsData,
        diagnostic: diagnosticText.split(',').map(d => d.trim()).filter(d => d)
      };

      if (visit) {
        await axios.put(`${API}/visits/${visit.id}`, dataToSend);
      } else {
        await axios.post(`${API}/visits`, dataToSend);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="soap-form-dialog">
        <DialogHeader>
          <DialogTitle>{visit ? 'Modifier la Consultation' : 'Nouvelle Consultation'}</DialogTitle>
          <DialogDescription>
            {visit ? 'Modifiez les informations de la consultation' : 'Enregistrez une nouvelle consultation avec notes SOAP et constantes'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Motif */}
          <div className="space-y-2">
            <Label htmlFor="motif">Motif de consultation *</Label>
            <Input
              id="motif"
              data-testid="motif-input"
              placeholder="Ex: Consultation de suivi - Fatigue persistante"
              value={formData.motif}
              onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
              required
            />
          </div>

          <Tabs defaultValue="soap" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="soap">Notes SOAP</TabsTrigger>
              <TabsTrigger value="vitals">Constantes</TabsTrigger>
            </TabsList>

            <TabsContent value="soap" className="space-y-4 mt-4">
              {/* Subjectif */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">S - Subjectif</CardTitle>
                  <p className="text-sm text-gray-500">Ce que le patient rapporte (symptômes, plaintes)</p>
                </CardHeader>
                <CardContent>
                  <Textarea
                    data-testid="soap-subjectif"
                    placeholder="Ex: Le patient se plaint de fatigue chronique depuis 3 mois..."
                    value={formData.soap.subjectif}
                    onChange={(e) => setFormData({
                      ...formData,
                      soap: { ...formData.soap, subjectif: e.target.value }
                    })}
                    rows={3}
                  />
                </CardContent>
              </Card>

              {/* Objectif */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">O - Objectif</CardTitle>
                  <p className="text-sm text-gray-500">Observations cliniques, examens physiques</p>
                </CardHeader>
                <CardContent>
                  <Textarea
                    data-testid="soap-objectif"
                    placeholder="Ex: Patient éveillé et alerte. Signes de fatigue visible..."
                    value={formData.soap.objectif}
                    onChange={(e) => setFormData({
                      ...formData,
                      soap: { ...formData.soap, objectif: e.target.value }
                    })}
                    rows={3}
                  />
                </CardContent>
              </Card>

              {/* Assessment */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">A - Assessment (Évaluation)</CardTitle>
                  <p className="text-sm text-gray-500">Diagnostic, interprétation</p>
                </CardHeader>
                <CardContent>
                  <Textarea
                    data-testid="soap-assessment"
                    placeholder="Ex: Fatigue chronique probablement liée au stress professionnel..."
                    value={formData.soap.assessment}
                    onChange={(e) => setFormData({
                      ...formData,
                      soap: { ...formData.soap, assessment: e.target.value }
                    })}
                    rows={3}
                  />
                </CardContent>
              </Card>

              {/* Plan */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">P - Plan</CardTitle>
                  <p className="text-sm text-gray-500">Plan de traitement, suivi</p>
                </CardHeader>
                <CardContent>
                  <Textarea
                    data-testid="soap-plan"
                    placeholder="Ex: Prescrire vitaminothérapie IV (cocktail Myers). Revoir dans 2 semaines..."
                    value={formData.soap.plan}
                    onChange={(e) => setFormData({
                      ...formData,
                      soap: { ...formData.soap, plan: e.target.value }
                    })}
                    rows={3}
                  />
                </CardContent>
              </Card>

              {/* Diagnostic */}
              <div className="space-y-2">
                <Label htmlFor="diagnostic">Diagnostic (séparés par des virgules)</Label>
                <Input
                  id="diagnostic"
                  data-testid="diagnostic-input"
                  placeholder="Ex: Fatigue chronique, Stress professionnel"
                  value={diagnosticText}
                  onChange={(e) => setDiagnosticText(e.target.value)}
                />
              </div>
            </TabsContent>

            <TabsContent value="vitals" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ta_systolique">TA Systolique (mmHg)</Label>
                  <Input
                    id="ta_systolique"
                    type="number"
                    data-testid="ta-systolique-input"
                    placeholder="Ex: 120"
                    value={formData.vitals.ta_systolique}
                    onChange={(e) => handleVitalsChange('ta_systolique', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ta_diastolique">TA Diastolique (mmHg)</Label>
                  <Input
                    id="ta_diastolique"
                    type="number"
                    data-testid="ta-diastolique-input"
                    placeholder="Ex: 80"
                    value={formData.vitals.ta_diastolique}
                    onChange={(e) => handleVitalsChange('ta_diastolique', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pouls">Pouls (bpm)</Label>
                  <Input
                    id="pouls"
                    type="number"
                    data-testid="pouls-input"
                    placeholder="Ex: 72"
                    value={formData.vitals.pouls}
                    onChange={(e) => handleVitalsChange('pouls', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temperature">Température (°C)</Label>
                  <Input
                    id="temperature"
                    type="number"
                    step="0.1"
                    data-testid="temperature-input"
                    placeholder="Ex: 36.8"
                    value={formData.vitals.temperature}
                    onChange={(e) => handleVitalsChange('temperature', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="spo2">SpO₂ (%)</Label>
                  <Input
                    id="spo2"
                    type="number"
                    data-testid="spo2-input"
                    placeholder="Ex: 98"
                    value={formData.vitals.spo2}
                    onChange={(e) => handleVitalsChange('spo2', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="poids">Poids (kg)</Label>
                  <Input
                    id="poids"
                    type="number"
                    step="0.1"
                    data-testid="poids-input"
                    placeholder="Ex: 75.5"
                    value={formData.vitals.poids}
                    onChange={(e) => handleVitalsChange('poids', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taille">Taille (cm)</Label>
                  <Input
                    id="taille"
                    type="number"
                    data-testid="taille-input"
                    placeholder="Ex: 175"
                    value={formData.vitals.taille}
                    onChange={(e) => handleVitalsChange('taille', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imc">IMC (calculé)</Label>
                  <Input
                    id="imc"
                    type="number"
                    step="0.1"
                    data-testid="imc-input"
                    value={formData.vitals.imc}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="save-visit-button"
              style={{
                background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
                color: 'white'
              }}
            >
              {loading ? 'Enregistrement...' : visit ? 'Mettre à jour' : 'Enregistrer la consultation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SOAPNoteForm;
